/* Deterministic math for lockstep multiplayer.
   Only +, -, *, /, sqrt, floor, min, max and abs are used: these are exactly specified by IEEE 754
   and ECMAScript, so every browser produces bit-identical results. Math.sin/cos/atan2/hypot and
   Math.random are not (engines use different implementations), so the simulation must use these. */

/** Seeded PRNG (mulberry32). Returns a function producing floats in [0, 1). */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const PI = 3.141592653589793, TAU = 6.283185307179586, HALF_PI = 1.5707963267948966;

/** sin(x), max abs error ~6e-8 after range reduction. */
export function dsin(x) {
  x -= TAU * Math.floor((x + PI) / TAU);
  if (x > HALF_PI) x = PI - x; else if (x < -HALF_PI) x = -PI - x;
  const x2 = x * x;
  return x * (1 + x2 * (-0.16666666666666666 + x2 * (0.008333333333333333 + x2 * (-0.0001984126984126984 + x2 * (2.7557319223985893e-6 + x2 * -2.505210838544172e-8)))));
}

/** cos(x) via phase shift. */
export function dcos(x) { return dsin(x + HALF_PI); }

/** atan2(y, x), max abs error ~1e-5 rad (plenty for steering and facing). */
export function datan2(y, x) {
  if (x === 0 && y === 0) return 0;
  const ax = x < 0 ? -x : x, ay = y < 0 ? -y : y;
  const a = (ax < ay ? ax : ay) / (ax < ay ? ay : ax);
  const s = a * a;
  let r = ((-0.0464964749 * s + 0.15931422) * s - 0.327622764) * s * a + a;
  if (ay > ax) r = HALF_PI - r;
  if (x < 0) r = PI - r;
  return y < 0 ? -r : r;
}

/** hypot for two values. */
export function hyp(a, b) { return Math.sqrt(a * a + b * b); }

/** FNV-1a over the raw bytes of typed arrays: identical state ⇒ identical hash. */
export function hashArrays(...arrays) {
  let h = 0x811c9dc5;
  for (const arr of arrays) {
    const b = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
    for (let i = 0; i < b.length; i++) { h ^= b[i]; h = Math.imul(h, 0x01000193); }
  }
  return h >>> 0;
}
